/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * Copyright 2023-2024 Highway9 Networks Inc.
 */
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState, store } from "..";
import { Types } from "../../constants/types";
import { radioCoreKPIsListService, metricsTemplateService, radioService, authenticationService } from "../../services";
import { alarmService } from "../../services/alarm-service";
import { Alarm, AlarmQuery } from "../../types/alarm";
import { RadioGroup } from "~/types/radioGroup";
import { ISite } from "~/types/site";
import Radio, { RadioX } from "../../types/radio";
import { RadioFilter } from "../../types/radioFilter";
import {
  getDeviceRunningData,
  getEdgeGaugeData,
  getRFGaugeData,
  getVMCGaugeData,
} from "../../views/radios/Graphs/graphHelper";
import { updateRadioDetails } from "../../views/radios/radioHelper";
import { fillTimeSeriesData } from "../../views/subscribers/graphs/graphHelper";
import { BAICELLS, HIGHWAY9 } from "~/constants";
import { observeStoreChange } from "../utils";
import { selectCompareEnable } from "./utilitySlice";
import { CoreKPIsList } from "~/types/metricGraph";
import { MetricsQuery } from "~/services/APIServices";
import { MetricsClient } from "~/sw/metrics/metricClient";
import { normalizeTime } from "~/helpers/utils";
import { metricHelper } from "~/helpers/metricHelper";
import { RadioUpgrade } from "~/types/AvailableUpgrades";


export const radioMetricWorker = new MetricsClient();
export const radioMetricCompareWorker = new MetricsClient();
export const radioSyncTimeWorker = new MetricsClient();
export const handoverMetricWorker = new MetricsClient();

// Option43 response types
export type Option43Result = {
  iscConfig?: string;
  hexString?: string;
  hexStringColonSeparated?: string;
};

export type Option43Response = {
  id?: string;
  result?: Option43Result | null;
};


type initState = {
  open: boolean;
  edit: boolean;
  installInfoOpen: boolean;
  rebootOpen: boolean;
  forceConfigOpen: boolean;
  installInfoDhcpTypeSelection: string;
  installInfoEdgeIp: string;
  option43Result: Option43Response | null;
  radioUpgradeOpen: boolean;
  loading: boolean;
  current: Radio | null;
  data: Array<RadioX | Radio>;
  completeData: Array<RadioX | Radio>;
  deleteOpen: boolean;
  count: number;
  upgrades: RadioUpgrade[];
  alarm: {
    loading: boolean;
    data: Array<Alarm>;
    count: number;
    /** @enum state ALL , ACTIVE */
    state: string;
    /** @enum carrier cell1 , cell2 */
    carrier: string;
  };
  import: {
    open: boolean;
    popupOpen: boolean;
    id: string | null;
    isRunning: boolean;
    data: any[];
    filteredData: any[];
    IRadioGroupData: RadioGroup[];
    IRadioSiteData: ISite[];
    dataCount: number;
    filteredDataCount: number;
    preserveSign: boolean;
  };
  export: {
    open: boolean;
    id: string | null;
    isRunning: boolean;
  };
  radioMonitoring: { operationID: string | null; }
  edgeGauge: {
    data: {
      connected: number;
      disconnected: number;
      unknown: number;
      partiallyConnected: number;
      thirdParty: number;
    };
  };
  rfGauge: {
    data: {
      good: number;
      bad: number;
      off: number;
      needAttention: number;
      unknown: number;
      thirdParty: number;
    };
  };

  vmcGauge: {
    data: {
      active: number;
      inactive: number;
      unknown: number;
      thirdParty: number;
    };
  };

  deviceRunning: {
    data: {
      1: number;
      2: number;
      3: number;
      4: number;
    };
  };

  countStatus: {
    green: number;
    orange: number;
    red: number;
    warning: number;
    unconfigured: number;
    thirdParty: number;
    ungrouped: number;
    borderRadio: number;
    total: number;
  };

  filter: {
    red: boolean;
    orange: boolean;
    green: boolean;
    warnings: boolean;
    unconfigured: boolean;
    thirdParty: boolean;
    ungrouped: boolean;
    borderRadio: boolean;
  };
  surveyFilter: {
    notKnown: boolean;
    feasible: boolean;
    notFeasible: boolean;
    feasibleWithChanges: boolean;
    proposed: boolean;
  };

  kpiExport: {
    open: boolean;
    operationID: string | null;
    isRunning: boolean;
  };

  kpiOptions: {
    cellId: number | string;
    plmn: string;
    vendor: string;
    templateId: string;
    technology: string;
  };

  metricsLoading: boolean;
  metricsError: string | null;
  metrics: {
    [key: string]: [number, number][];
  };

  compareMetrics: {
    [key: string]: [number, number][];
  };

  aggregatedMetrics: {
    [key: string]: [number, number][];
  };

  /** RadioID to RadioNames */
  radioMap: Record<string, string>; // radioId: radioName

  radioCoreKPIList: CoreKPIsList[];

  exportKPIId: string | null;
  loadingExportKPI: boolean;

  radioBulkAssignOpen: boolean;
  carrierKPIExportOpen: boolean;

  // Handover metrics for neighbor radios
  handoverMetrics: {
    loading: boolean;
    error: string | null;
    data: HandoverMetricsData;
  };
};

const initialState: initState = {
  open: false,
  edit: false,
  installInfoOpen: false,
  rebootOpen: false,
  forceConfigOpen: false,
  installInfoDhcpTypeSelection: "isc-dhcp-server",
  installInfoEdgeIp: "",
  option43Result: null,

  loading: true,
  current: null,
  data: [],
  completeData: [],
  deleteOpen: false,
  count: 0,
  upgrades: [],
  alarm: {
    loading: true,
    data: [],
    count: 0,
    state: "ACTIVE",
    carrier: "all",
  },

  import: {
    open: false,
    popupOpen: false,
    id: null,
    isRunning: false,
    data: [],
    filteredData: [],
    IRadioGroupData: [],
    IRadioSiteData: [],
    dataCount: 0,
    filteredDataCount: 0,
    preserveSign: true,
  },
  export: {
    open: false,
    id: null,
    isRunning: false,
  },
  radioMonitoring: { operationID: null },
  edgeGauge: {
    data: {
      connected: 0,
      disconnected: 0,
      unknown: 0,
      partiallyConnected: 0,
      thirdParty: 0,
    },
  },
  rfGauge: {
    data: {
      good: 0,
      bad: 0,
      off: 0,
      unknown: 0,
      thirdParty: 0,
      needAttention: 0,
    },
  },
  vmcGauge: {
    data: {
      active: 0,
      inactive: 0,
      unknown: 0,
      thirdParty: 0,
    },
  },
  deviceRunning: {
    data: {
      1: 0,
      2: 0,
      3: 0,
      4: 0,
    },
  },
  countStatus: {
    green: 0,
    orange: 0,
    red: 0,
    warning: 0,
    unconfigured: 0,
    thirdParty: 0,
    ungrouped: 0,
    borderRadio: 0,
    total: 0,
  },

  filter: {
    red: true,
    orange: true,
    green: true,
    warnings: true,
    unconfigured: true,
    thirdParty: true,
    ungrouped: true,
    borderRadio: true,
  },
  surveyFilter: {
    notKnown: true,
    feasible: true,
    notFeasible: true,
    feasibleWithChanges: true,
    proposed: true,
  },

  kpiExport: {
    open: false,
    operationID: null,
    isRunning: false,
  },
  kpiOptions: {
    cellId: "1|2",
    plmn: "",
    vendor: HIGHWAY9,
    technology: "4G",
    templateId: '',
  },
  metricsLoading: true,
  metricsError: null,
  metrics: {},
  compareMetrics: {},
  aggregatedMetrics: {},
  radioMap: {},
  radioUpgradeOpen: false,

  radioCoreKPIList: [],
  exportKPIId: null,
  loadingExportKPI: false,
  radioBulkAssignOpen: false,
  carrierKPIExportOpen: false,

  // Handover metrics initial state
  handoverMetrics: {
    loading: false,
    error: null,
    data: {},
  },
};

export const fetchRadioMetrics = createAsyncThunk(`radio/fetchRadioMetrics`, async (query: MetricsQuery, thunkApi) => {
  try {
    const state = thunkApi.getState() as RootState;
    const { interval, timeZone } = state.utility.time;

    const tz = query._tz || timeZone;

    query.interval = {
      startTime: normalizeTime(query.interval.startTime, query.resolution, tz),
      endTime: normalizeTime(query.interval.endTime, query.resolution, tz)
    }

    let data: typeof initialState.metrics = {};

    await radioMetricWorker.initialize('radios', query, authenticationService.currentUserValue!.token, (id, metrics) => {
      data = metrics as typeof initialState.metrics;
    }, { noPolling: interval === 'custom', interval: 30 });
    return data;
  } catch (err: any) {
    console.log("Error in fetchRadioMetrics", err);
    const errorMsg = err.errors[0].message;
    return thunkApi.rejectWithValue(errorMsg)
  }
});
export const fetchRadioMetricsCompare = createAsyncThunk(`radio/fetchRadioMetricsCompare`, async (query: MetricsQuery, thunkApi) => {
  try {
    const state = thunkApi.getState() as RootState;
    const { interval, timeZone } = state.utility.time;
    query.interval = {
      startTime: normalizeTime(query.interval.startTime, query.resolution, timeZone),
      endTime: normalizeTime(query.interval.endTime, query.resolution, timeZone)
    }

    let data: typeof initialState.metrics = {};

    await radioMetricCompareWorker.initialize('radios', query, authenticationService.currentUserValue!.token, (id, metrics) => {
      data = metrics as typeof initialState.metrics;
    }, { noPolling: interval === 'custom', interval: 30 });
    return data;
  } catch (err: any) {
    console.log("Error in fetchRadioMetricsCompare", err);
    const errorMsg = err.errors[0].message;
    return thunkApi.rejectWithValue(errorMsg)
  }
});
export const fetchRadioMetricsTemplate = createAsyncThunk(`radio/fetchRadioMetricsTemplate`, metricsTemplateService.getTemplates);
export const fetchRadioMetricsAggregate = createAsyncThunk(
  `radio/fetchRadioMetricsAggregate`,
  radioService.getMetricsAggregate
);
export const fetchRadioCoreKPIList = createAsyncThunk(`radio/fetchRadioCoreKPIList`, radioCoreKPIsListService.getRadioCoreKPIsList);

export const fetchRadios = createAsyncThunk(`radio/fetchRadios`, async (query?: { timestamp?: number }) => {
  const timestamp = query?.timestamp;
  try {
    const query = timestamp ? { timestamp } : {};
    const data = await radioService.getRadios(query);
    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const fetchRadioAlarms = createAsyncThunk(`radio/fetchRadioAlarms`, async (query: AlarmQuery) => {
  try {
    const alarms = await alarmService.getAlarms(query);
    return alarms;
  } catch (error) {
    console.error(error);
    return [];
  }
});

export const fetchRadioUpgrades = createAsyncThunk(`radio/fetchRadioUpgrades`, async () => {
  try {
    const data = await radioService.getRadioUpgrades();
    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const copyRadioName = createAsyncThunk(`radio/copyName`, (_: void, { getState }) => {
  const state = getState() as RootState;
  const radio = state.radio.current;
  if (radio) {
    const name = radio.name;
    navigator.clipboard.writeText(name);
    return name;
  }
});

export const copyRadioSerial = createAsyncThunk(`radio/copySerial`, (_: void, { getState }) => {
  const state = getState() as RootState;
  const radio = state.radio.current;
  if (radio) {
    const serial = radio.serialNumber;
    navigator.clipboard.writeText(serial ?? "");
    return serial;
  }
});

export interface HandoverMetricsQuery {
  radioId: string;
  neighborEcis: string[];
  vendorType?: string;
  cellId?: string;
}

export type HandoverMetricsData = {
  [neighborEci: string]: {
    timestamp: number;
    percentage: number | null;
    successCount: number | null;
    attemptCount: number | null;
  }[];
};

function processHandoverMetrics(metrics: Record<string, {id: string, data: [number, number][]}[]> ) {
  const result: HandoverMetricsData = {};

  // Collect data by neighborEci and metric type
  const dataByEci: Record<string, {
    success: Map<number, number | null>;
    attempt: Map<number, number | null>;
  }> = {};

  Object.entries(metrics).forEach(([key, value]) => {
    const isSuccess = key.includes("succsum");
    const isAttempt = key.includes("attsum");
    if (!isSuccess && !isAttempt) return;

    value.forEach(item => {
      // Extract neighborEci from id format: radio-uuid#neighborEci=123
      const neighborEci = item.id.split('#')[1]?.split(',')
        .find(l => l.startsWith('neighborEci='))?.split('=')[1];

      if (!neighborEci) return;

      // Initialize maps for this neighbor
      if (!dataByEci[neighborEci]) {
        dataByEci[neighborEci] = {
          success: new Map(),
          attempt: new Map()
        };
      }

      const map = isSuccess ? dataByEci[neighborEci].success : dataByEci[neighborEci].attempt;

      // Store data points, aggregating multiple values for same timestamp
      item.data.forEach(([ts, val]) => {
        if (val !== null) {
          map.set(ts, (map.get(ts) || 0) + val);
        } else if (!map.has(ts)) {
          map.set(ts, null);
        }
      });
    });
  });

  // Create time-series array for each neighborEci
  Object.entries(dataByEci).forEach(([neighborEci, { success, attempt }]) => {
    // Get all unique timestamps from both metrics
    const timestamps = new Set([...success.keys(), ...attempt.keys()]);

    result[neighborEci] = Array.from(timestamps)
      .sort((a, b) => a - b)
      .map(timestamp => {
        const successCount = success.get(timestamp) ?? null;
        const attemptCount = attempt.get(timestamp) ?? null;
        const percentage = (successCount !== null && attemptCount !== null && attemptCount > 0)
          ? (successCount / attemptCount) * 100
          : null;

        return { timestamp, percentage, successCount, attemptCount };
      });
  });

  return result;
}

export const fetchHandoverMetrics = createAsyncThunk(
  `radio/fetchHandoverMetrics`,
  async (query: HandoverMetricsQuery, thunkApi) => {
    try {
      const state = thunkApi.getState() as RootState;
      const { startTime, endTime, aggregationPeriod, timeZone , interval} = state.utility.time;

      const neighborEciString = query.neighborEcis.join(",");
      const resolution = metricHelper.getAggregationPeriod(aggregationPeriod);

      const metricsQuery: MetricsQuery = {
        metrics: [
          "sum(radio_outsuccsum{neighborEci=~\"keys.neighboreci\", cell=~\"keys.cellId\"})",
          "sum(radio_outattsum{neighborEci=~\"keys.neighboreci\" ,cell=~\"keys.cellId\"})",
        ],
        interval: {
          startTime: normalizeTime(startTime, resolution, timeZone),
          endTime: normalizeTime(endTime, resolution, timeZone),
        },
        resolution,

        ids: [query.radioId],
        queryKeys: [
          { key: "vendor", value: query.vendorType || "AIRSPAN" },
          { key: "cellId", value: query.cellId || "1|2" },
          { key: "neighborEci", value: neighborEciString },
        ],
        _showLoading: false,
      };

      let rawData: Record<string, any> = {};

      await handoverMetricWorker.initialize('radios', metricsQuery, authenticationService.currentUserValue!.token,
        (id, metrics) => {
          rawData = metrics as Record<string, any>;
        },
        { noPolling: interval === "custom", name: 'handover' }
      );

      // Normalize worker output so both single-series and multi-series metrics
      // use the same { id, data }[] shape expected by processHandoverMetrics.
      const normalized: Record<string, { id: string; data: [number, number][] }[]> = {};

      Object.entries(rawData).forEach(([metricKey, value]) => {
        if (!Array.isArray(value) || value.length === 0) {
          normalized[metricKey] = [];
          return;
        }

        const first: any = value[0];

        // Case: worker returned simple [timestamp, value][] array (single series, no id)
        if (Array.isArray(first) && first.length === 2) {
          const neighborEcis = neighborEciString.split(",");
          const neighborLabel =
            neighborEcis.length === 1 && neighborEcis[0]
              ? `neighborEci=${neighborEcis[0]}`
              : "";

          const id = neighborLabel
            ? `${query.radioId}#${neighborLabel}`
            : `${query.radioId}`;

          normalized[metricKey] = [
            {
              id,
              data: value as [number, number][],
            },
          ];

          return;
        }

        // Case: worker already returned [{ id, data }, ...]
        normalized[metricKey] = value as { id: string; data: [number, number][] }[];
      });

      // Process and return the metrics
      return processHandoverMetrics(normalized);
    } catch (err: any) {
      console.error("Error fetching handover metrics:", err);
      return thunkApi.rejectWithValue(err?.message || "Failed to fetch handover metrics");
    }
  }
);

const radioSlice = createSlice({
  name: Types.radio,
  initialState,
  reducers: {
    setOpen: (state, action: PayloadAction<boolean>) => {
      state.open = action.payload;
    },
    setEdit: (state, action: PayloadAction<boolean>) => {
      state.edit = action.payload;
    },
    setValues: (state, action: PayloadAction<Radio | null>) => {
      state.current = action.payload;
    },
    setData: (state, action: PayloadAction<Radio[]>) => {
      state.data = action.payload;
    },
    updateData: (state, action: PayloadAction<Radio>) => {
      const index = state.data.findIndex((item) => item.id === action.payload.id);
      state.data[index] = action.payload;
    },
    addData: (state, action: PayloadAction<Radio>) => {
      state.data.push(action.payload);
    },
    delete: (state, action: PayloadAction<string>) => {
      const index = state.data.findIndex((item) => item.id === action.payload);
      state.data = state.data.filter((_, i) => i !== index);
    },
    setDeleteOpen: (state, action: PayloadAction<boolean>) => {
      state.deleteOpen = action.payload;
    },
    setCount: (state, action: PayloadAction<number>) => {
      state.count = action.payload;
    },
    setInstallInfoOpen: (state, action: PayloadAction<boolean>) => {
      state.installInfoOpen = action.payload;
    },
    setInstallInfoDhcpTypeSelection: (state, action: PayloadAction<string>) => {
      state.installInfoDhcpTypeSelection = action.payload;
    },
    setInstallInfoEdgeIp: (state, action: PayloadAction<string>) => {
      state.installInfoEdgeIp = action.payload;
    },
    setOption43Result: (state, action: PayloadAction<any>) => {
      state.option43Result = action.payload;
    },
    setRebootOpen: (state, action: PayloadAction<boolean>) => {
      state.rebootOpen = action.payload;
    },
    setForceConfigOpen: (state, action: PayloadAction<boolean>) => {
      state.forceConfigOpen = action.payload;
    },
    setUpgradeRadioOpen: (state, action: PayloadAction<boolean>) => {
      state.radioUpgradeOpen = action.payload;
    },
    setOpenImport: (state, action: PayloadAction<boolean>) => {
      state.import.open = action.payload;
    },
    setImportData: (state, action: PayloadAction<any[]>) => {
      state.import.data = action.payload;
      state.import.dataCount = action.payload.length;
      state.import.filteredData = action.payload.filter(
        (item: any) => item["Action (ADD/UPDATE/DELETE)"] === "UPDATE" || item["Action (ADD/UPDATE/DELETE)"] === "DELETE" || item["Action (ADD/UPDATE/DELETE)"] === "ADD"
      );
      state.import.filteredDataCount = state.import.filteredData.length;
    },
    setImportPopupOpen: (state, action: PayloadAction<boolean>) => {
      state.import.open = action.payload;
    },
    setImportId: (state, action: PayloadAction<string | null>) => {
      state.import.id = action.payload;
    },
    setImportRadioRunning: (state, action: PayloadAction<boolean>) => {
      state.import.isRunning = action.payload;
    },
    updateIRadioSiteUpdate: (state, action: PayloadAction<ISite[]>) => {
      state.import.IRadioSiteData = action.payload;
    },
    updateIRadioGroupUpdate: (state, action: PayloadAction<{ data: RadioGroup; index: number }>) => {
      const { data, index } = action.payload;
      state.import.IRadioGroupData[index] = data;
    },

    setPreserveSign: (state, action: PayloadAction<boolean>) => {
      state.import.preserveSign = action.payload;
    },
    setFilter: (state, action: PayloadAction<{ type: RadioFilter; value: boolean }>) => {
      state.filter[action.payload.type] = action.payload.value;
    },
    toggleFilter: (state, action: PayloadAction<RadioFilter>) => {
      state.filter[action.payload] = !state.filter[action.payload];
    },
    selectFilter: (state, action: PayloadAction<RadioFilter>) => {
      state.filter[action.payload] = true;
      // deselect all other filters
      Object.keys(state.filter).forEach((key) => {
        if (key !== action.payload) {
          state.filter[key as RadioFilter] = false;
        }
      });
    },
    clearFilter: (state) => {
      Object.keys(state.filter).forEach((key) => {
        state.filter[key as RadioFilter] = true;
      });
    },
    setSurveyFilter: (state, action: PayloadAction<{ type: string; value: boolean }>) => {
      state.surveyFilter[action.payload.type as keyof typeof state.surveyFilter] = action.payload.value;
    },
    toggleSurveyFilter: (state, action: PayloadAction<string>) => {
      const filterType = action.payload as keyof typeof state.surveyFilter;
      state.surveyFilter[filterType] = !state.surveyFilter[filterType];
    },
    setExportOpen: (state, action: PayloadAction<boolean>) => {
      state.export.open = action.payload;
    },
    setRadioMonitoringOperationID(state, action: PayloadAction<string | null>) {
      state.radioMonitoring.operationID = action.payload;
    },
    setExportId: (state, action: PayloadAction<string | null>) => {
      state.export.id = action.payload;
    },
    setExportIsRunning: (state, action: PayloadAction<boolean>) => {
      state.export.isRunning = action.payload;
    },

    setAlarmCarrier: (state, action: PayloadAction<string>) => {
      state.alarm.carrier = action.payload;
    },
    setAlarmState: (state, action: PayloadAction<string>) => {
      state.alarm.state = action.payload;
    },
    setKPIExportOpen(state, action: PayloadAction<boolean>) {
      state.kpiExport.open = action.payload;
    },
    setKPIExportID(state, action: PayloadAction<string | null>) {
      state.kpiExport.operationID = action.payload;
    },
    setKPIExportRunning(state, action: PayloadAction<boolean>) {
      state.kpiExport.isRunning = action.payload;
    },

    // kpi options
    setKPICellId(state, action: PayloadAction<number | string>) {
      state.kpiOptions.cellId = action.payload;
    },
    setKPIPlmn(state, action: PayloadAction<string>) {
      state.kpiOptions.plmn = action.payload;
    },
    setKPITemplateId(state, action: PayloadAction<string>) {
      state.kpiOptions.templateId = action.payload;
    },
    setKPITechnology(state, action: PayloadAction<string>) {
      state.kpiOptions.technology = action.payload;
    },
    setKPIVendor(state, action: PayloadAction<string>) {
      if (action.payload === BAICELLS) {
        state.kpiOptions.vendor = HIGHWAY9;
      } else {
        state.kpiOptions.vendor = action.payload;
      }
    },


    clearCompareMetrics(state) {
      state.compareMetrics = {}
    },
    setExportKPIId(state, action: PayloadAction<string | null>) {
      state.exportKPIId = action.payload
    },
    setLoadingExportKPI(state, action: PayloadAction<boolean>) {
      state.loadingExportKPI = action.payload
    },
    setBulkAssignOpen(state, action: PayloadAction<boolean>) {
      state.radioBulkAssignOpen = action.payload;
    },
    setCarrierKPIExportOpen(state, action: PayloadAction<boolean>) {
      state.carrierKPIExportOpen = action.payload;
    },

    // Handover metrics actions
    clearHandoverMetrics(state) {
      state.handoverMetrics.data = {};
      state.handoverMetrics.error = null;
    },
  },


  extraReducers: (builder) => {
    builder
      .addCase(fetchRadios.fulfilled, (state, action) => {
        // Store complete data
        state.completeData = action.payload as RadioX[];

        // Calculate both data and count using complete data
        const { data, count } = updateRadioDetails(action.payload as RadioX[], state.filter);
        state.data = data;
        state.count = data.length;
        state.countStatus = count;
        // map ids to names
        state.radioMap = data.reduce((map, item) => {
          map[item.id!] = item.name;
          return map;
        }, {} as Record<string, string>);

        // update graphs data
        updateGraphs(state, data);

        // sync the current radio state with the updated data
        if (state.current?.id && !state.open) {
          const currRadio = state.data.find((item) => item.id === state.current?.id);
          if (currRadio) {
            state.current = currRadio;
          }
        }
        state.loading = false;
      })
      .addCase(fetchRadios.rejected, (state, action) => {
        state.loading = false;
        state.data = [];
        state.count = 0;
        // If we throw error, then the state reset is not executed
        console.error(action.error);
      })

      .addCase(fetchRadioAlarms.fulfilled, (state, action) => {
        state.alarm.data = action.payload;
        state.alarm.count = action.payload.length;
        state.alarm.loading = false;
      })

      .addCase(fetchRadioAlarms.rejected, (state, action) => {
        state.alarm.loading = false;
        state.alarm.data = [];
        console.error(action.error);
        throw action.error;
      })

      // fetch radio Upgrades
      .addCase(fetchRadioUpgrades.fulfilled, (state, action) => {
        state.upgrades = action.payload;
      })

      .addCase(fetchRadioMetrics.pending, (state, action) => {
        // When we start a new metrics fetch (e.g. switching radio/site/template),
        // optionally show loading and clear existing series so old graphs don't
        // stay visible while new data is loading.
        if (action.meta.arg._showLoading) {
          state.metricsLoading = true;
          state.metrics = {};
          state.compareMetrics = {};
        }
      })

      .addCase(fetchRadioMetrics.fulfilled, (state, action) => {
        const data = action.payload;

        Object.keys(data).forEach((key) => {
          state.metrics[key] = data[key];
        });

        state.metricsLoading = false;
        state.metricsError = null;
      })

      .addCase(fetchRadioMetricsCompare.fulfilled, (state, action) => {
        const data = action.payload;

        Object.keys(data).forEach((key) => {
          state.compareMetrics[key] = data[key];
        });

        state.metricsLoading = false;
        state.metricsError = null;
      })

      .addCase(fetchRadioMetrics.rejected, (state, action) => {
        state.metricsLoading = false;
        state.metricsError = action.payload as string
      })

      .addCase(fetchRadioMetricsTemplate.rejected, (state, action) => {
        console.log(action.error)
        throw action.error
      })
      .addCase(fetchRadioCoreKPIList.fulfilled, (state, action) => {
        state.radioCoreKPIList = action.payload as unknown as CoreKPIsList[]
      })
      .addCase(fetchRadioCoreKPIList.rejected, (state, action) => {
        console.log(action.error)
        throw action.error
      })
      .addCase(fetchRadioMetricsAggregate.fulfilled, (state, action) => {
        const data = action.payload;
        data.forEach((obj) => {
          if (!obj.metricData.length) return [];
          try {
            const metricData = fillTimeSeriesData({
              data: obj.metricData,
              startTime: action.meta.arg.interval.startTime,
              endTime: action.meta.arg.interval.endTime,
              interval: action.meta.arg.resolution,
              fillType: "previous",
            });
            state.aggregatedMetrics[obj.metric] = metricData;
          } catch (e) {
            console.error("Error in fillTimeSeries", e, obj.metric, obj.metricData);
            const metricData = obj.metricData.length ? obj.metricData : [];
            state.aggregatedMetrics[obj.metric] = metricData;
          }
        });
      })

      .addCase(fetchRadioMetricsAggregate.rejected, (state, action) => {
        console.error(action.error);
        throw action.error;
      })

      // Handover metrics
      .addCase(fetchHandoverMetrics.pending, (state) => {
        state.handoverMetrics.loading = true;
        state.handoverMetrics.error = null;
      })
      .addCase(fetchHandoverMetrics.fulfilled, (state, action) => {
        state.handoverMetrics.loading = false;
        state.handoverMetrics.data = action.payload;
        state.handoverMetrics.error = null;
      })
      .addCase(fetchHandoverMetrics.rejected, (state, action) => {
        state.handoverMetrics.loading = false;
        state.handoverMetrics.error = action.payload as string;
      });
  },
});

export const radioActions = radioSlice.actions;
export default radioSlice.reducer;
export const radioState = (state: RootState) => state.radio.current;
export const radioData = (state: RootState) => state.radio.data;
export const radioOpen = (state: RootState) => state.radio.open;
export const radioEdit = (state: RootState) => state.radio.edit;

export const radioUpgradeOpen = (state: RootState) => state.radio.radioUpgradeOpen;
export const radioUpgrades = (state: RootState) => state.radio.upgrades;

export const radioDeleteOpen = (state: RootState) => state.radio.deleteOpen;
export const radioCount = (state: RootState) => state.radio.count;
export const radioLoading = (state: RootState) => state.radio.loading;
export const radioByID = (id: string) => (state: RootState) => state.radio.data.find((radio) => radio.id === id) || null;

export const radioInstallInfoOpen = (state: RootState) => state.radio.installInfoOpen;
export const radioRebootOpen = (state: RootState) => state.radio.rebootOpen;
export const radioForceConfigureOpen = (state: RootState) => state.radio.forceConfigOpen;
export const radioInstallInfoDhcpTypeSelection = (state: RootState) => state.radio.installInfoDhcpTypeSelection;
export const radioInstallInfoEdgeIp = (state: RootState) => state.radio.installInfoEdgeIp;
export const radioOption43Result = (state: RootState) => state.radio.option43Result;

export const radioImportOpen = (state: RootState) => state.radio.import.open;
export const radioImportData = (state: RootState) => state.radio.import.data;
export const radioImportDataCount = (state: RootState) => state.radio.import.dataCount;
export const radioPreserveSign = (state: RootState) => state.radio.import.preserveSign;
export const radioImportFilteredData = (state: RootState) => state.radio.import.filteredData;
export const radioImportFilteredDataCount = (state: RootState) => state.radio.import.filteredDataCount;
export const ImportRadioGroupData = (state: RootState) => state.radio.import.IRadioGroupData;
export const ImportRadioSiteData = (state: RootState) => state.radio.import.IRadioSiteData;

export const radioImportPopupOpen = (state: RootState) => state.radio.import.popupOpen;
export const radioImportId = (state: RootState) => state.radio.import.id;
export const radioImportRunning = (state: RootState) => state.radio.import.isRunning;

export const radioEdgeGaugeData = (state: RootState) => state.radio.edgeGauge.data;
export const radioRFGaugeData = (state: RootState) => state.radio.rfGauge.data;
export const radioVMCGaugeData = (state: RootState) => state.radio.vmcGauge.data;
export const radioDeviceRunningData = (state: RootState) => state.radio.deviceRunning.data;

export const radioCountStatus = (state: RootState) => state.radio.countStatus;
export const radioFilter = (state: RootState) => state.radio.filter;
export const radioSurveyFilter = (state: RootState) => state.radio.surveyFilter;

export const radioExportOpen = (state: RootState) => state.radio.export.open;
export const radioExportId = (state: RootState) => state.radio.export.id;
export const radioExportIsRunning = (state: RootState) => state.radio.export.isRunning;

export const radioAlarm = (state: RootState) => state.radio.alarm;

export const radioMetrics = (state: RootState) => state.radio.metrics;
export const radioMetricsError = (state: RootState) => state.radio.metricsError;
export const radioCompareMetrics = (state: RootState) => state.radio.compareMetrics;
export const radioAggregatedMetrics = (state: RootState) => state.radio.aggregatedMetrics;
export const radioMetricsLoading = (state: RootState) => state.radio.metricsLoading;

export const radioMetric = (metric: string) => (state: RootState) => state.radio.metrics[metric];
export const radioAggregatedMetric = (metric: string) => (state: RootState) => state.radio.aggregatedMetrics[metric];

export const radioMap = (state: RootState) => state.radio.radioMap;
export const radioKPIOption = (state: RootState) => state.radio.kpiOptions;

export const exportKPIId = (state: RootState) => state.radio.exportKPIId;
export const loadingExportKPI = (state: RootState) => state.radio.loadingExportKPI;

export const getRadioCoreKPIList = (state: RootState) => state.radio.radioCoreKPIList

export const radioBulkAssignOpen = (state: RootState) => state.radio.radioBulkAssignOpen;
export const radioCarrierKPIExportOpen = (state: RootState) => state.radio.carrierKPIExportOpen;

// Handover metrics selectors
export const handoverMetricsData = (state: RootState) => state.radio.handoverMetrics.data;
export const handoverMetricsLoading = (state: RootState) => state.radio.handoverMetrics.loading;
export const handoverMetricsError = (state: RootState) => state.radio.handoverMetrics.error;
export const handoverMetricByEci = (eci: string) => (state: RootState) => state.radio.handoverMetrics.data[eci];
/**
 * Lookup helper: given a radio name, return its site and group names.
 * Case-insensitive match on radio name; returns null if no radio found.
 */
export const selectRadioSiteAndGroupByName =
  (radioName: string) =>
    (state: RootState): { siteName: string; siteId: string; groupName: string; groupId: string } | null => {
      const target = state.radio.completeData.find(
        (r) => r.name?.toLowerCase() === radioName?.toLowerCase()
      );
      if (!target) return null;
      return {
        siteName: target.radioSite?.name ?? "",
        siteId: target.radioSite?.id ?? "",
        groupName: target.radioGroup?.name ?? "",
        groupId: target.radioGroup?.id ?? "",
      };
    };

function updateGraphs(state: initState, data: Radio[]) {
  // update edge gauge data
  const edgeGaugeData = getEdgeGaugeData(data);
  state.edgeGauge.data = edgeGaugeData;

  // update rf gauge data
  const rfGaugeData = getRFGaugeData(data);
  state.rfGauge.data = rfGaugeData;

  // update vmc gauge data
  const vmcGaugeData = getVMCGaugeData(data);
  state.vmcGauge.data = vmcGaugeData;

  const deviceRunningData = getDeviceRunningData(data);
  state.deviceRunning.data = deviceRunningData;
}


// clear compare metrics when compare is disabled
setTimeout(() => {

  radioMetricWorker.eventEmitter().on('worker:data', async (id, workerID, metrics) => {
    store.dispatch({ type: 'radio/fetchRadioMetrics/fulfilled', payload: metrics });
  });
  radioMetricCompareWorker.eventEmitter().on('worker:data', async (id, workerID, metrics) => {
    store.dispatch({ type: 'radio/fetchRadioMetricsCompare/fulfilled', payload: metrics });
  });

  radioMetricWorker.eventEmitter().on('worker:error', async (id, workerID, error) => {
    store.dispatch({ type: 'radio/fetchRadioMetrics/rejected', payload: error });
  });
  radioMetricCompareWorker.eventEmitter().on('worker:error', async (id, workerID, error) => {
    store.dispatch({ type: 'radio/fetchRadioMetricsCompare/rejected', payload: error });
  });

  // Handover metrics worker event listeners
  handoverMetricWorker.eventEmitter().on('worker:data', async (id, workerID, metrics) => {
    const state = store.getState();
    const neighborEcis = Object.keys(state.radio.handoverMetrics.data);
    if (neighborEcis.length > 0) {
      store.dispatch({ type: 'radio/fetchHandoverMetrics/fulfilled', payload: metrics });
    }
  });
  handoverMetricWorker.eventEmitter().on('worker:error', async (id, workerID, error) => {
    store.dispatch({ type: 'radio/fetchHandoverMetrics/rejected', payload: error });
  });

  observeStoreChange(selectCompareEnable, (state) => {
    if (!state) {
      store.dispatch(radioActions.clearCompareMetrics())
    }
  })
})